import { StethoscopeIcon } from "assets/icons/Stethoscope";
import { LoadingSpinner } from "components/LoadingSpinner";
import { AccountFeeType } from "constants/fee";
import { validationConstants } from "constants/formValidation";
import { ToastContext } from "context/ToastContext";
import { calculateClearPatientFee } from "helpers/fee";
import {
  calculateDiscount,
  centsToDollars,
  dollarsToCents,
  parsePrice,
} from "helpers/price";
import { useGetAccountDiscountAndFee } from "hooks/useGetAccountDiscountAndFee";
import usePriceNew from "hooks/usePrice";
import {
  useProviderProcedure,
  // CPT_SEARCH_REQUIRES_VISIT_DATE_MESSAGE,
} from "hooks/useProviderProcedure";
import { useQuery } from "hooks/useQuery";
import { sizer } from "layout/styles/styled/sizer";
import { isEmpty, debounce } from "lodash";
import {
  convertToUTC,
  formatDateOfServiceForProviderProcedureSearch,
  // isDateOfServiceValidForCptSearch,
} from "pages/CreateOrder/utils/helpers";
import { AutoCompleteSelectParams } from "primereact/autocomplete";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useContext,
  useRef,
} from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "store";
import { useGetAccountByIdQuery } from "store/queries/account";
import { useGetCptCodeQuantityRecommendationsMutation } from "store/queries/cptCodeQuantityRecommendations";
import { useCheckPriorAuthQuery } from "store/queries/priorAuthorizations";
import {
  useSearchProviderProcedureMutation,
  useGetCptRecommendationsMutation,
} from "store/queries/providerProcedure";
import {
  setPriceBreakdownRows,
  setRows,
  IPriceBreakdownRows,
  setAutoCompleteValue,
  setSelectedProcedures,
  addSelectedProcedures,
  setCptRecommendations,
  setLoadingCptRecommendations,
  clearCptRecommendations,
  setCptQuantityRecommendations,
  setLoadingCptQuantityRecommendations,
  clearCptQuantityRecommendations,
} from "store/slices/serviceDetails";
import styled from "styled-components";
import { TourAnchor } from "training/anchors";
import { IPriorAuthFinding } from "types/PriorAuth/PriorAuth";

import { ProcedureRow } from "../../../../../../types/CreateOrder";
import {
  SpecialtyList,
  SpecialtyListDict,
} from "../../../../../../types/Provider/Provider";
import { StepCard } from "../../../StepCard";
import { StepKey } from "../../../StepCard/helper";
import { useStepStatus } from "../../../StepCard/useStepStatus";
import {
  buildGFETotalAmount,
  buildDescriptionColumnTemplate,
  buildSelectedProceduresPriceBreakdownToTable,
  buildSelectedProceduresObject,
  buildSelectedProceduresToTable,
} from "../../builders";
import {
  autoCompleteFieldHandleOnSelectProcedure,
  autoCompleteHandleOnUnSelectProcedureNew,
  quantityChangeHandleOnSelectProcedure,
} from "../../handlers";
import { getCptQuantities } from "../../helpers";
import {
  buildUnavailableCptMessage,
  classifyPayerChange,
  fetchPayerProcedures,
} from "../../payerRefetch";
import { AutoCompleteCPT } from "../Common/AutoCompleteCPT";
import { BenefitCell } from "../Common/BenefitCell";
import { CptCodePill } from "../Common/CptCodePill";
import { buildCptGroups, CptGroup } from "../Common/cptGroups";
import { CptRecommendations } from "../Common/CptRecommendations";
import { ImplantSuggestion } from "../Common/ImplantSuggestion";
import { NoServices } from "../Common/NoServices";
import { ProviderDropdown } from "../Common/ProviderDropdown";
import { QuantityCell } from "../Common/QuantityCell";
import { RemoveItem } from "../Common/RemoveItem";
import { ServicesFooter } from "../Common/ServicesFooter";
import {
  StyledAmountField,
  StyledAmountTooltip,
  StyledBundlePanel,
  StyledBundleRow,
  StyledNumberInput,
  StyledServicesTable,
  StyledTrashButton,
} from "../Common/styled";
import { AdjustmentDetails } from "./AdjustmentDetails";
import { PatientResponsibilityDetails } from "./PatientResponsibilityDetails";

export const GFEFieldsContainer = styled.div`
  display: flex;
  justify-content: end;
  margin-top: ${sizer(4)};
`;

export const CardHeader = styled.div``;

const PRICE_NEEDS_REVIEW = "Price needs to be reviewed and updated.";

export function GFEServiceDetails({
  redo = false,
  draft = false,
  copy = false,
  totalAmount,
  initialProviderProcedures,
}: {
  redo?: boolean;
  draft?: boolean;
  copy?: boolean;
  totalAmount?: number;
  initialProviderProcedures?: any;
}) {
  const query = useQuery();
  const dispatch = useDispatch();
  const status = useStepStatus(StepKey.SERVICES);
  const { id, draftId } = useParams<{ id?: string; draftId?: string }>();
  const orderId = redo || draft ? id || draftId : undefined;

  const accountId = query.get("accountId") || undefined;

  const { setValue, watch, getValues, register } = useFormContext();
  const { current: toastElement } = useContext(ToastContext);
  const [searchMutation] = useSearchProviderProcedureMutation();
  const [getCptRecommendations] = useGetCptRecommendationsMutation();
  const [getCptCodeQuantityRecommendations] =
    useGetCptCodeQuantityRecommendationsMutation();
  // CLE-2690: the codes the current payer does not carry, and whether the last refetch
  // actually removed them from the table (a payer change does, the visit-date refetch on
  // redo/draft/copy does not). The banner wording differs between the two.
  const [unavailableCptCodes, setUnavailableCptCodes] = useState<string[]>([]);
  const [unavailableCptRemoved, setUnavailableCptRemoved] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loadingCptCode, setLoadingCptCode] = useState<string | null>(null);
  const [currentProviderProcedures, setCurrentProviderProcedures] =
    useState<any>(initialProviderProcedures || []);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const excludedNcciRulesetIds: string[] =
    watch("excludedNcciRulesetIds") || [];
  const setExcludedNcciRulesetIds = (
    updater: string[] | ((prev: string[]) => string[])
  ) => {
    const newVal =
      typeof updater === "function" ? updater(excludedNcciRulesetIds) : updater;
    setValue("excludedNcciRulesetIds", newVal);
  };
  const excludedNcciInfo: Array<{
    rulesetId: string;
    cptCode: string;
    discountedCptCode: string;
  }> = watch("excludedNcciInfo") || [];
  const setExcludedNcciInfo = (
    updater:
      | Array<{ rulesetId: string; cptCode: string; discountedCptCode: string }>
      | ((
          prev: Array<{
            rulesetId: string;
            cptCode: string;
            discountedCptCode: string;
          }>
        ) => Array<{
          rulesetId: string;
          cptCode: string;
          discountedCptCode: string;
        }>)
  ) => {
    const newVal =
      typeof updater === "function" ? updater(excludedNcciInfo) : updater;
    setValue("excludedNcciInfo", newVal);
  };

  const payerNameField: string = watch("payerName");
  const payerField = watch("payer");
  const serviceDetailsField = watch("serviceDetails");
  const deductibleField: number = watch("deductible");
  const outOfPocketMaxField: number = watch("outOfPocketMax");
  const flatCopayField: number = watch("flatCopay");
  const coinsurancePercentField: number = watch("coinsurancePercent");
  const facilityCopayField = watch("facilityCopay");
  const facilityCoinsuranceField = watch("facilityCoinsurance");
  const facilityDeductibleField = watch("facilityDeductible");
  const professionalFlatCopayField = watch("professionalFlatCopay");
  const showProfessionalCopayField = watch("showProfessionalCopay");
  const professionalCopayField = watch("professionalCopay");
  const professionalCoinsuranceField = watch("professionalCoinsurance");
  const professionalDeductibleField = watch("professionalDeductible");
  const dateOfServiceField = watch("dateOfService");
  const notScheduledYetField = watch("notScheduledYet");
  const benefitDetailsField = watch("benefitDetails");
  /* Strict Visit date before CPT search / refetch (re-enable with AutoComplete + effect guard below):
  const orderTypeField = watch("orderType");
  const dateOfServiceValidForCptSearch = useMemo(
    () => isDateOfServiceValidForCptSearch(dateOfServiceField, orderTypeField),
    [dateOfServiceField, orderTypeField]
  );
  const dateOfServiceForSearch = useMemo(
    () =>
      dateOfServiceValidForCptSearch
        ? formatDateOfServiceForProviderProcedureSearch(dateOfServiceField)
        : undefined,
    [dateOfServiceField, dateOfServiceValidForCptSearch]
  );
  const canSearchCptByVisitDate = dateOfServiceValidForCptSearch;
  */
  const dateOfServiceForSearch = useMemo(
    () =>
      notScheduledYetField
        ? undefined
        : formatDateOfServiceForProviderProcedureSearch(dateOfServiceField),
    [dateOfServiceField, notScheduledYetField]
  );
  const firstNameField = watch("firstName");
  const lastNameField = watch("lastName");
  const dateOfBirthField = watch("dateOfBirth");
  // secondary insurance fields
  const isSecondaryInsuranceActivatedField = watch(
    "secondaryInsuranceActivated"
  );
  const deductible2Field: number = watch("deductible2");
  const outOfPocketMax2Field: number = watch("outOfPocketMax2");
  const flatCopay2Field: number = watch("flatCopay2");
  const professionalFlatCopay2Field: number = watch("professionalFlatCopay2");
  const showProfessionalCopay2Field = watch("showProfessionalCopay2");
  const coinsurancePercent2Field: number = watch("coinsurancePercent2");
  const facilityCopay2Field = watch("facilityCopay2");
  const facilityCoinsurance2Field = watch("facilityCoinsurance2");
  const facilityDeductible2Field = watch("facilityDeductible2");
  const professionalCopay2Field = watch("professionalCopay2");
  const professionalCoinsurance2Field = watch("professionalCoinsurance2");
  const professionalDeductible2Field = watch("professionalDeductible2");
  // CLE-2605 — the secondary payer itself, so the server can tell whether it absorbs the whole
  // balance the primary left. Watched (not just read at submit) so switching the secondary payer
  // re-prices the estimate.
  const payer2Field = watch("payer2");
  const totalPatientEstimateField = watch("amount");
  const initialClearPatientFeeValueInPercentageField = watch(
    "initialClearPatientFeeValueInPercentage"
  );

  const {
    getGFEPrices,
    priceBreakdown,
    insuranceAmounts,
    getInsuranceAmounts,
  } = usePriceNew();

  const { accountDiscount: discount, isLoading: isDiscountLoading } =
    useGetAccountDiscountAndFee(AccountFeeType.ClearHealthDiscountForGFE);

  const { procedures, providerProcedures, search } = useProviderProcedure({
    payerNameField,
    dateOfService: dateOfServiceForSearch,
  });

  const {
    rows = [],
    autoCompleteValue = [],
    selectedProcedures = [],
    dataTableRows = [],
    displayAdjustments = false,
    displayPatientResponsibility = false,
    priceBreakdownRows = {} as IPriceBreakdownRows,
    loadingPriceBreakdown: priceExecuting,
  } = useSelector((state: RootState) => state.serviceDetailsSlice);

  // The refetch effects only re-run on their own dep, so what they close over goes stale —
  // reading a stale (often empty) `rows` is what collapsed every CPT back to quantity 1.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const autoCompleteValueRef = useRef(autoCompleteValue);
  autoCompleteValueRef.current = autoCompleteValue;
  /**
   * CLE-2766 — prior-authorization warnings for the codes on this estimate.
   *
   * One batched call for every code on the order, not one per row: this table re-renders on every
   * keystroke in a quantity box, and a per-row query would multiply that by the number of lines.
   *
   * Skipped entirely until there is a payer and at least one code, so an estimate that has not got
   * that far makes no request at all. `planName` is whatever eligibility reported; when it has not
   * run this is null and the API answers "unconfirmed" for plan-scoped lists rather than guessing.
   */
  const priorAuthCptCodes = useMemo(
    () => [
      ...new Set(rows.map((row: ProcedureRow) => row.cptCode).filter(Boolean)),
    ],
    [rows]
  );

  const { data: priorAuthData } = useCheckPriorAuthQuery(
    {
      payerId: payerField?.payerId,
      planName:
        benefitDetailsField?.response?.planCoverageSummary?.PlanName ?? null,
      cptCodes: priorAuthCptCodes,
      accountId,
    },
    { skip: !payerField?.payerId || priorAuthCptCodes.length === 0 }
  );

  /**
   * Findings keyed by code. A failed or in-flight request leaves this empty, which renders no
   * badges — the warning is advisory and must never be able to block or break an estimate.
   */
  const priorAuthByCode = useMemo(() => {
    const map: Record<string, IPriorAuthFinding> = {};
    (priorAuthData?.data ?? []).forEach((finding) => {
      map[finding.cptCode] = finding;
    });
    return map;
  }, [priorAuthData]);

  const previousPayerRef = useRef<string | null>(null);

  // For the payer specific account fees, we need to fetch account data to get them
  const { data: accountData } = useGetAccountByIdQuery(accountId || "", {
    skip: !accountId,
  });

  // CLE-2695 — the common-code recommendations ("Commonly paired with" / "Common Quantity") are
  // now account-controlled. Default true: they shipped ungated, so only an explicit false turns
  // them off and accounts that never had the flag written keep seeing them.
  const commonCodeRecommendationsEnabled =
    accountData?.data?.features?.commonCodeRecommendations !== false;

  // And we calculate Clear Patient Fee based on selected payer, if it exists
  // For redo/draft: ALWAYS use the order's saved value from the order table.
  //   - If the order has a fee (number > 0): use it
  //   - If the order has no fee (null or 0): use 0, do NOT fall back to account
  // For initial creation/copy: look up from account table
  // CLE-2204: CPF basis is the POST-discount patient responsibility, matching the backend's
  // breakdown calculation (services/order.helpers.ts GFE path). Previously CPF was computed
  // on the pre-discount procedure total ($1500), then the whole {procedure + CPF} was
  // discounted together — which embedded a non-$5-aligned CPF into order.amountInCents and
  // produced a 50¢-ish drift vs the backend's post-discount basis result. Now: apply the
  // GFE discount to the procedure subtotal FIRST, then compute CPF on the discounted basis.
  const { clearPatientFee, clearPatientFeePercentage } = useMemo(() => {
    if (!totalPatientEstimateField) {
      return { clearPatientFee: 0, clearPatientFeePercentage: 0 };
    }

    // Apply GFE discount to the procedure subtotal before computing CPF (CLE-2204).
    const discountedPatientEstimate = calculateDiscount(
      totalPatientEstimateField,
      discount
    );

    // For redo/draft: always use the order's saved value, never the account
    // (order has no clearPatientFee when value is null or 0)
    if (redo || draft) {
      const percentage = initialClearPatientFeeValueInPercentageField ?? 0;
      const feeAmount =
        percentage > 0
          ? calculateClearPatientFee(discountedPatientEstimate, percentage)
          : 0;
      return {
        clearPatientFee: feeAmount,
        clearPatientFeePercentage: percentage,
      };
    }

    // For initial creation/copy: look up from account's payer fees
    if (!payerField?.payerId || !accountData?.data?.payerAccountFees) {
      return { clearPatientFee: 0, clearPatientFeePercentage: 0 };
    }
    const payerPatientFee = accountData.data.payerAccountFees.find(
      (fee) =>
        fee.payerId === payerField.payerId && fee.type === "ClearPatientFee"
    );

    if (!payerPatientFee) {
      return { clearPatientFee: 0, clearPatientFeePercentage: 0 };
    }

    // Calculate fee with rounding up to nearest $5, against the discounted patient estimate
    const feeAmount = calculateClearPatientFee(
      discountedPatientEstimate,
      payerPatientFee.valueInPercentage
    );
    return {
      clearPatientFee: feeAmount,
      clearPatientFeePercentage: payerPatientFee.valueInPercentage,
    };
  }, [
    payerField?.payerId,
    accountData?.data?.payerAccountFees,
    totalPatientEstimateField,
    discount,
    redo,
    draft,
    initialClearPatientFeeValueInPercentageField,
  ]);

  const actionTemplate = (row: ProcedureRow) => {
    return <RemoveItem row={row} autoCompleteValue={autoCompleteValue} />;
  };

  const providerDropdownTemplate = (row: ProcedureRow) => {
    return (
      <ProviderDropdown
        row={row}
        currentProviderProcedures={currentProviderProcedures}
      />
    );
  };

  useEffect(() => {
    register("totalAmount", {
      required: validationConstants.SERVICE_DETAILS.GFE_CLEAR_PRICE,
    });
  }, []);

  useEffect(() => {
    setValue("isDiscountLoading", isDiscountLoading);
  }, [isDiscountLoading]);

  useEffect(
    () => () => {
      setValue("isDiscountLoading", false);
    },
    []
  );

  useEffect(() => {
    if (isEmpty(rows)) getInsuranceAmounts(undefined);
    setValue(
      "serviceDetails",
      rows.map((row: ProcedureRow) => ({
        code: row.cptCode,
        specialty: row.specialty,
        amountInCents: row.amount,
        procedureId: row.procedureId,
        cptDescription: row.description,
        providerId: row.selectedProvider.id,
        providerName: row.selectedProvider.name,
        providerProcedureId: row.providerProcedureId,
        benefitsForProvProc: row.benefitSelectionType
          ? {
              benefitSelectionType: row.benefitSelectionType,
            }
          : undefined,
      }))
    );
    // We need to set clearPatientFee in form data so it can be used to calculate total patientresponsibilityamountincents in service details
    setValue("clearPatientFee", clearPatientFee || 0);
    setValue("clearPatientFeePercentage", clearPatientFeePercentage || 0);
  }, [rows, clearPatientFee, clearPatientFeePercentage]);

  const isValidDate = (dateStr: string) => {
    if (!dateStr) return false;
    // Check if string matches MM/DD/YYYY format
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    return dateRegex.test(dateStr);
  };

  // Clear excluded NCCI state when the user manually changes procedures.
  // We track the previous procedure count to distinguish user edits from initial load.
  const prevProcCountRef = useRef<number | null>(null);
  useEffect(() => {
    const currentCount = Array.isArray(serviceDetailsField)
      ? serviceDetailsField.length
      : 0;
    if (
      prevProcCountRef.current !== null &&
      prevProcCountRef.current !== currentCount &&
      currentCount > 0 &&
      prevProcCountRef.current > 0
    ) {
      setExcludedNcciRulesetIds([]);
      setExcludedNcciInfo([]);
    }
    prevProcCountRef.current = currentCount;
  }, [serviceDetailsField]);

  useEffect(() => {
    if (isEmpty(serviceDetailsField)) {
      setPriceBreakdownRows({ adjustments: [], patientResponsibility: [] });
      return;
    }

    const costApplicationRules = {
      facilityRules: {
        useCopay: facilityCopayField,
        useCoinsurance: facilityCoinsuranceField,
        useDeductible: facilityDeductibleField,
      },
      nonFacilityRules: {
        useCopay: professionalCopayField,
        useCoinsurance: professionalCoinsuranceField,
        useDeductible: professionalDeductibleField,
      },
    };
    const costApplicationRules2 = {
      facilityRules: {
        useCopay: facilityCopay2Field,
        useCoinsurance: facilityCoinsurance2Field,
        useDeductible: facilityDeductible2Field,
      },
      nonFacilityRules: {
        useCopay: professionalCopay2Field,
        useCoinsurance: professionalCoinsurance2Field,
        useDeductible: professionalDeductible2Field,
      },
    };

    getGFEPrices({
      serviceDetails: { procedures: serviceDetailsField },
      remainingDeductibleInDollar: deductibleField,
      outOfPocketMaxInDollar: outOfPocketMaxField,
      flatCopayInDollar: flatCopayField,
      professionalFlatCopayInDollar: professionalFlatCopayField,
      showProfessionalCopay: showProfessionalCopayField,
      coinsurancePercent: coinsurancePercentField,
      costApplicationRules,
      remainingDeductible2InDollar: deductible2Field,
      outOfPocketMax2InDollar: outOfPocketMax2Field,
      flatCopay2InDollar: flatCopay2Field,
      professionalFlatCopay2InDollar: professionalFlatCopay2Field,
      showProfessionalCopay2: showProfessionalCopay2Field,
      coinsurancePercent2: coinsurancePercent2Field,
      costApplicationRules2,
      secondaryPayerId: payer2Field?.payerId ?? null,
      isSecondaryInsuranceActivated: isSecondaryInsuranceActivatedField,
      dateOfService: isValidDate(dateOfServiceField)
        ? convertToUTC(dateOfServiceField)
        : null,
      patient: {
        firstName: firstNameField,
        lastName: lastNameField,
        dateOfBirth: isValidDate(dateOfBirthField)
          ? convertToUTC(dateOfBirthField)
          : null,
      },
      orderId: orderId || null,
      excludedNcciRulesetIds,
    });
    setValue("gfeQuoteId", null);
  }, [
    serviceDetailsField,
    deductibleField,
    outOfPocketMaxField,
    flatCopayField,
    coinsurancePercentField,
    facilityCopayField,
    facilityCoinsuranceField,
    facilityDeductibleField,
    professionalFlatCopayField,
    professionalCopayField,
    professionalCoinsuranceField,
    professionalDeductibleField,
    dateOfServiceField,
    firstNameField,
    lastNameField,
    dateOfBirthField,
    deductible2Field,
    outOfPocketMax2Field,
    flatCopay2Field,
    professionalFlatCopay2Field,
    coinsurancePercent2Field,
    facilityCopay2Field,
    facilityCoinsurance2Field,
    facilityDeductible2Field,
    professionalCopay2Field,
    professionalCoinsurance2Field,
    professionalDeductible2Field,
    showProfessionalCopay2Field,
    isSecondaryInsuranceActivatedField,
    // CLE-2605 — switching the secondary payer can change who absorbs the primary's balance,
    // so the estimate has to be recalculated on it, not just on the benefit amounts.
    payer2Field?.payerId,
    excludedNcciRulesetIds,
  ]);

  useEffect(() => {
    if (initialProviderProcedures?.length) {
      setCurrentProviderProcedures(initialProviderProcedures);
    }
  }, [initialProviderProcedures]);

  useEffect(() => {
    buildSelectedProceduresPriceBreakdownToTable({ priceBreakdown, dispatch });

    // Sync excludedNcciInfo from the pricing response (handles draft reload case
    // where the info wasn't persisted but the backend reconstructs it from ruleset IDs)
    if (
      (priceBreakdown as any)?.excludedNcciInfo?.length > 0 &&
      excludedNcciInfo.length === 0
    ) {
      setExcludedNcciInfo((priceBreakdown as any).excludedNcciInfo);
    }
    // The deductible-reduction banner used to be filled in here from the price response, which
    // meant it needed a CPT first. It now comes from useMatchingOrders (CLE-2769 follow-up).
  }, [priceBreakdown]);
  console.log("insuranceAmounts", insuranceAmounts);

  useEffect(() => {
    const totalAmountForGFE = buildGFETotalAmount(
      priceBreakdownRows.patientResponsibility
    );
    setValue("amount", centsToDollars(totalAmountForGFE));
    setValue(
      "totalInsurancePortion",
      centsToDollars(insuranceAmounts.totalInsurancePayInCents)
    );
    setValue(
      "totalInsuranceAllowable",
      centsToDollars(insuranceAmounts.totalAllowedInsuranceAmount)
    );
    // Read by the Estimate Results panel; mapFormToRequestBody names its fields
    // explicitly, so these never reach the API.
    setValue(
      "totalDeductible",
      centsToDollars(insuranceAmounts.totalDeductibleInCents)
    );
    setValue(
      "totalCoinsurance",
      centsToDollars(insuranceAmounts.totalCoinsuranceInCents)
    );
    setValue(
      "totalCopay",
      centsToDollars(insuranceAmounts.totalFlatCopayInCents)
    );

    // CLE-2204: discount applies to the procedure subtotal only; CPF is layered on top of the
    // already-discounted base. clearPatientFee above was computed on the discounted patient
    // estimate, so order.totalAmount = discountedProcedure + CPF matches the backend's
    // post-discount-basis breakdown calculation.
    const discountedTotalInCents = calculateDiscount(
      totalAmountForGFE,
      discount
    );
    setValue("discountPercent", discount);
    setValue(
      "discountAmount",
      centsToDollars(totalAmountForGFE - discountedTotalInCents)
    );
    const totalAmountInCents =
      discountedTotalInCents + dollarsToCents(clearPatientFee);
    setValue("totalAmount", centsToDollars(totalAmountInCents));
  }, [
    priceBreakdownRows,
    redo,
    totalAmount,
    discount,
    insuranceAmounts,
    clearPatientFee,
  ]);

  /* When strict Visit date is required, also require dateOfServiceForSearch before refetch:
  if (!dateOfServiceForSearch) return;
  */
  const refetchProviderProcedures = async ({
    applyToTable,
  }: {
    applyToTable: boolean;
  }) => {
    if (!accountId || !autoCompleteValueRef.current.length) return;

    setSearchLoading(true);
    try {
      const { unavailableCptCodes, nextRows, nextAutoCompleteValue } =
        await fetchPayerProcedures({
          accountId,
          payerName: payerNameField,
          dateOfService: dateOfServiceForSearch,
          autoCompleteValue: autoCompleteValueRef.current,
          getPreviousRows: () => rowsRef.current,
          search: (args) => searchMutation(args) as Promise<any>,
        });

      setUnavailableCptCodes(unavailableCptCodes);
      setUnavailableCptRemoved(applyToTable);
      if (applyToTable) {
        dispatch(setRows(nextRows));
        // CLE-2690: the chips go with the rows. Leaving a chip for a code the new payer
        // does not carry resurrects the line — the quantity control and the implant
        // suggestions both read `autoCompleteValue` rather than the rows.
        dispatch(setAutoCompleteValue(nextAutoCompleteValue));
        dispatch(setSelectedProcedures(nextAutoCompleteValue));
      }
    } catch (error) {
      console.error("Failed to refresh provider procedures", error);
    } finally {
      setSearchLoading(false);
    }
  };

  // A payer change always rebuilds: the rows' providerProcedureIds and prices belong to the
  // previous payer until it lands, and saving from that state persists them.
  useEffect(() => {
    const changeKind = classifyPayerChange({
      previousPayer: previousPayerRef.current,
      nextPayer: payerNameField,
    });
    // Recorded before the bail-out: on a new order the payer is picked before any CPT
    // exists, and skipping the record would make the next selection look like the first.
    if (payerNameField) previousPayerRef.current = payerNameField;
    if (changeKind !== "changed") return;

    refetchProviderProcedures({ applyToTable: true });
  }, [payerNameField]);

  // A visit-date change only refreshes the "not found" warning on redo/draft/copy: amounts
  // may have been hand-edited on the original order. Prices still re-run via getGFEPrices.
  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    refetchProviderProcedures({ applyToTable: !(redo || draft || copy) });
  }, [dateOfServiceForSearch]);

  useEffect(() => {
    if (!autoCompleteValue.length) {
      setCurrentProviderProcedures([]);
    }
  }, [autoCompleteValue]);

  // Fetch CPT recommendations when autoCompleteValue changes
  useEffect(() => {
    if (
      !commonCodeRecommendationsEnabled ||
      !accountId ||
      !autoCompleteValue.length
    ) {
      dispatch(clearCptRecommendations());
      dispatch(clearCptQuantityRecommendations());
      return;
    }

    const fetchRecommendations = async () => {
      const includedCptCodes = buildSelectedProceduresObject(
        autoCompleteValue
      ).map((item) => item.code);

      dispatch(setLoadingCptRecommendations(true));
      dispatch(setLoadingCptQuantityRecommendations(true));

      const fetchPaired = async () => {
        try {
          const response = await getCptRecommendations({
            accountId,
            includedCptCodes,
            payerName: payerNameField || undefined,
          });
          if ("data" in response && response.data?.data?.recommendations) {
            dispatch(setCptRecommendations(response.data.data.recommendations));
          } else {
            dispatch(clearCptRecommendations());
          }
        } catch (error) {
          console.error("Error fetching CPT recommendations:", error);
          dispatch(clearCptRecommendations());
        }
      };

      const fetchQuantity = async () => {
        try {
          const response = await getCptCodeQuantityRecommendations({
            accountId,
            includedCptCodes,
          });
          if ("data" in response && response.data?.data?.recommendations) {
            dispatch(
              setCptQuantityRecommendations(response.data.data.recommendations)
            );
          } else {
            dispatch(clearCptQuantityRecommendations());
          }
        } catch (error) {
          console.error("Error fetching CPT quantity recommendations:", error);
          dispatch(clearCptQuantityRecommendations());
        }
      };

      await Promise.all([fetchPaired(), fetchQuantity()]);
    };

    // Debounce the API call to avoid too many requests
    const timeoutId = setTimeout(fetchRecommendations, 500);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeoutId);
  }, [
    accountId,
    autoCompleteValue,
    commonCodeRecommendationsEnabled,
    dispatch,
    getCptRecommendations,
    getCptCodeQuantityRecommendations,
    payerNameField,
  ]);

  // CLE-2690: an effect here used to hide the unavailable-CPT banner whenever the table
  // emptied, on the assumption that an empty table meant a stale warning. Now that the
  // refetch removes the codes the payer does not carry, an empty table is the *result* the
  // banner explains, so clearing it there left the user with no reason for it. The banner
  // is cleared by the next CPT search instead, and overwritten by each refetch.
  console.log("rows", rows);

  const onAmountChange = useCallback(
    (value: number | null, row: ProcedureRow) => {
      const convertedValue = value ? dollarsToCents(value) : 0;
      const updatedRows = [...rows].map((r) => {
        if (
          r.cptCode === row.cptCode &&
          r.description === row.description &&
          r.specialty === row.specialty &&
          r.providerProcedureId === row.providerProcedureId &&
          JSON.stringify(
            [...r.providerDropdown].sort((a, b) => a.id.localeCompare(b.id))
          ) ===
            JSON.stringify(
              [...row.providerDropdown].sort((a, b) => a.id.localeCompare(b.id))
            ) &&
          JSON.stringify(r.selectedProvider) ===
            JSON.stringify(row.selectedProvider)
        ) {
          return {
            ...r,
            amount: convertedValue,
          };
        }
        return r;
      });
      dispatch(setRows(updatedRows));
    },
    [rows, dispatch]
  );

  const debouncedOnAmountChange = useMemo(
    () => debounce(onAmountChange, 500),
    [onAmountChange]
  );

  const cptGroups = useMemo(
    () => buildCptGroups(dataTableRows),
    [dataTableRows]
  );

  const expandedBundles = useMemo(
    () =>
      cptGroups.reduce(
        (acc, group) =>
          group.isBundle ? { ...acc, [group.cptCode]: true } : acc,
        {} as Record<string, boolean>
      ),
    [cptGroups]
  );

  const cptCodeTemplate = (group: CptGroup) => (
    <CptCodePill code={group.cptCode} />
  );

  const benefitTemplate = (group: CptGroup) => (
    <BenefitCell row={group.rows[0]} rows={rows} dispatch={dispatch} isGFE />
  );

  const quantityTemplate = (group: CptGroup) => (
    <QuantityCell
      row={group.rows[0]}
      rows={rows}
      autoCompleteValue={autoCompleteValue}
      dispatch={dispatch}
      selectedProcedures={selectedProcedures}
      procedures={procedures}
      providerProcedures={providerProcedures}
    />
  );

  const editableAmountTemplate = (row: any) => {
    const alertId = `amountAlert-${row.id}`;

    return (
      <StyledAmountField $needsReview={!!row.toBeEdited}>
        <StyledNumberInput
          currency="USD"
          locale="en-US"
          mode="currency"
          min={0}
          value={centsToDollars(row.amount)}
          onChange={(e) => debouncedOnAmountChange(e.value, row)}
        />

        {row.toBeEdited && (
          <>
            <i
              id={alertId}
              className="pi pi-exclamation-circle amount-alert"
              data-testid="amountNeedsReview"
            />
            <StyledAmountTooltip
              target={`#${alertId}`}
              position="top"
              content={PRICE_NEEDS_REVIEW}
            />
          </>
        )}
      </StyledAmountField>
    );
  };

  const providerCellTemplate = (group: CptGroup) =>
    group.isBundle ? (
      <span style={{ color: "var(--brand-gray-600)" }}>Multiple providers</span>
    ) : (
      providerDropdownTemplate(group.rows[0])
    );

  const amountCellTemplate = (group: CptGroup) =>
    group.isBundle ? (
      <span className="font-semibold" style={{ color: "var(--brand-black)" }}>
        {parsePrice(centsToDollars(group.totalInCents))}
      </span>
    ) : (
      editableAmountTemplate(group.rows[0])
    );

  const removeCptGroup = (group: CptGroup) => {
    // The autocomplete stores "<code> - <default description>", so take the
    // description from there rather than the row, which the user may have edited.
    const defaultDescription = buildSelectedProceduresObject(
      autoCompleteValue
    ).find((procedure) => procedure.code === group.cptCode)?.cptDescription;

    autoCompleteHandleOnUnSelectProcedureNew({
      e: {
        value: `${group.cptCode} - ${defaultDescription}`,
      } as AutoCompleteSelectParams,
      autoCompleteValue,
      rows,
      dispatch,
      selectedProcedures,
    });

    setCurrentProviderProcedures(
      currentProviderProcedures?.filter(
        (pp: any) => pp?.procedure?.cptCode !== group.cptCode
      )
    );
  };

  const actionCellTemplate = (group: CptGroup) =>
    group.isBundle ? (
      <StyledTrashButton
        aria-label="Remove service"
        icon="pi pi-trash"
        className="p-button-text p-button-rounded"
        onClick={() => removeCptGroup(group)}
      />
    ) : (
      actionTemplate(group.rows[0])
    );

  const bundleTemplate = (group: CptGroup) => (
    <StyledBundlePanel className="px-3 py-3">
      <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <span
          className="flex align-items-center gap-2 text-sm font-semibold"
          style={{ color: "var(--brand-purple-700)" }}
        >
          <i className="pi pi-clone" />
          Bundled provider pricing
        </span>

        <span className="text-sm" style={{ color: "var(--brand-gray-600)" }}>
          Bundle total {parsePrice(centsToDollars(group.totalInCents))}
        </span>
      </div>

      <div className="flex align-items-center gap-3 mb-2 px-3">
        <span
          className="text-xs font-semibold uppercase"
          style={{
            width: "170px",
            color: "var(--brand-gray-600)",
            letterSpacing: "0.06em",
          }}
        >
          Amount
        </span>
        <span
          className="text-xs font-semibold uppercase"
          style={{
            width: "220px",
            color: "var(--brand-gray-600)",
            letterSpacing: "0.06em",
          }}
        >
          Specialty
        </span>
        <span
          className="flex-1 text-xs font-semibold uppercase"
          style={{ color: "var(--brand-gray-600)", letterSpacing: "0.06em" }}
        >
          Provider
        </span>
      </div>

      <div className="flex flex-column gap-2">
        {group.rows.map((providerRow) => (
          <StyledBundleRow
            key={providerRow.id}
            className="flex align-items-center gap-3 px-3 py-2"
          >
            <div style={{ width: "170px", flexShrink: 0 }}>
              {editableAmountTemplate(providerRow)}
            </div>
            <div style={{ width: "220px", flexShrink: 0 }}>
              {SpecialtyListDict[providerRow.specialty as SpecialtyList]}
            </div>
            <div className="flex-1">
              {providerDropdownTemplate(providerRow)}
            </div>
            {actionTemplate(providerRow)}
          </StyledBundleRow>
        ))}
      </div>
    </StyledBundlePanel>
  );

  const handleRecommendationClick = async (cptCode: string) => {
    try {
      // Set loading state for this specific CPT code
      setLoadingCptCode(cptCode);

      // Search for the CPT code and get the results directly
      const searchResults = await search({ query: cptCode });

      if (!searchResults || searchResults.length === 0) {
        console.warn(`No search results found for CPT code: ${cptCode}`);
        return;
      }

      // Find the procedure in the search results
      const providerProcedure = searchResults.find(
        (pp) => pp.procedure?.cptCode === cptCode
      );
      if (!providerProcedure?.procedure) {
        console.warn(`Procedure not found for CPT code: ${cptCode}`);
        return;
      }

      const { procedure } = providerProcedure;

      // Create the selection value in the format expected by AutoComplete
      const selectionValue = `${cptCode} - ${procedure.description}`;

      // Check if this procedure is already selected
      if (autoCompleteValue.includes(selectionValue)) {
        console.log(`CPT code ${cptCode} is already selected`);
        return;
      }

      // Create a mock AutoCompleteSelectParams event
      const mockEvent = {
        value: selectionValue,
        originalEvent: {
          target: null,
          currentTarget: null,
          type: "select",
          nativeEvent: new Event("select"),
          isDefaultPrevented: () => false,
          isPropagationStopped: () => false,
          persist: () => {},
        } as any,
      };

      // Use the same handler that AutoCompleteCPT uses
      const arrayOfCptCodes = [...selectedProcedures];
      const filteredCpt = arrayOfCptCodes.filter((p) => p === selectionValue);

      // Update current provider procedures if needed
      if (currentProviderProcedures && setCurrentProviderProcedures) {
        const copy = [...currentProviderProcedures];
        const existingIds = new Set(copy.map((pp) => pp.id));
        searchResults.forEach((providerProcedure) => {
          if (!existingIds.has(providerProcedure.id)) {
            copy.push(providerProcedure);
          }
        });
        setCurrentProviderProcedures(copy);
      }

      // Call the proper selection handler with the search results
      await autoCompleteFieldHandleOnSelectProcedure({
        e: mockEvent,
        dispatch,
        selectedProcedures,
        procedures: searchResults.map((pp) => pp.procedure).filter(Boolean),
        providerProcedures: searchResults,
        rows,
        newValue: filteredCpt.length + 1,
      });
    } catch (error) {
      console.error(
        `Error handling recommendation click for CPT ${cptCode}:`,
        error
      );
    } finally {
      // Clear loading state
      setLoadingCptCode(null);
    }
  };

  const handleQuantityRecommendationClick = async (
    cptCode: string,
    suggestedQuantity: number
  ) => {
    try {
      setLoadingCptCode(cptCode);

      const matchingRow = rows.find((r) => r.cptCode === cptCode);
      if (!matchingRow) {
        console.warn(`No row found for CPT code: ${cptCode}`);
        return;
      }

      await quantityChangeHandleOnSelectProcedure({
        e: {
          value: `${matchingRow.cptCode} - ${matchingRow.description}`,
        } as AutoCompleteSelectParams,
        selectedProcedures,
        procedures,
        providerProcedures,
        rows,
        dispatch,
        newValue: suggestedQuantity,
        row: matchingRow,
      });
    } catch (error) {
      console.error(
        `Error handling quantity recommendation click for CPT ${cptCode}:`,
        error
      );
    } finally {
      setLoadingCptCode(null);
    }
  };

  const handleAddImplant = async (
    cptCode: string,
    suggestedAmount?: number
  ) => {
    try {
      setLoadingCptCode(cptCode);

      const searchResults = await search({ query: cptCode });

      if (!searchResults || searchResults.length === 0) {
        toastElement?.show({
          severity: "warn",
          summary: "Implant Not Found",
          detail: `Implant code ${cptCode} is not set up for this facility. Please add it to the fee schedule first.`,
          life: 5000,
        });
        return;
      }

      const providerProcedure = searchResults.find(
        (pp) => pp.procedure?.cptCode === cptCode
      );
      if (!providerProcedure?.procedure) {
        toastElement?.show({
          severity: "warn",
          summary: "Implant Not Found",
          detail: `Implant code ${cptCode} is not set up for this facility. Please add it to the fee schedule first.`,
          life: 5000,
        });
        return;
      }

      const { procedure } = providerProcedure;
      const selectionValue = `${cptCode} - ${procedure.description}`;

      if (autoCompleteValue.includes(selectionValue)) {
        toastElement?.show({
          severity: "info",
          summary: "Already Added",
          detail: `Implant code ${cptCode} is already in the estimate.`,
          life: 3000,
        });
        return;
      }

      const mockEvent = {
        value: selectionValue,
        originalEvent: {
          target: null,
          currentTarget: null,
          type: "select",
          nativeEvent: new Event("select"),
          isDefaultPrevented: () => false,
          isPropagationStopped: () => false,
          persist: () => {},
        } as any,
      };

      const arrayOfCptCodes = [...selectedProcedures];
      const filteredCpt = arrayOfCptCodes.filter((p) => p === selectionValue);

      if (currentProviderProcedures && setCurrentProviderProcedures) {
        const copy = [...currentProviderProcedures];
        const existingIds = new Set(copy.map((pp) => pp.id));
        searchResults.forEach((pp) => {
          if (!existingIds.has(pp.id)) {
            copy.push(pp);
          }
        });
        setCurrentProviderProcedures(copy);
      }

      await autoCompleteFieldHandleOnSelectProcedure({
        e: mockEvent,
        dispatch,
        selectedProcedures,
        procedures: searchResults.map((pp) => pp.procedure).filter(Boolean),
        providerProcedures: searchResults,
        rows,
        newValue: filteredCpt.length + 1,
      });

      // If a suggested amount was provided, update the newly added row's amount
      if (suggestedAmount !== undefined) {
        // Small delay to ensure the row has been added to Redux
        setTimeout(() => {
          // Get the current rows from Redux and update the implant's amount
          const updatedRows = rows
            .concat(
              buildSelectedProceduresToTable({
                selectedProceduresObject: buildSelectedProceduresObject([
                  selectionValue,
                ]),
                procedures: searchResults
                  .map((pp) => pp.procedure)
                  .filter(Boolean),
                providerProcedures: searchResults,
                rows: [],
                row: undefined,
              })
            )
            .map((r) => {
              if (r.cptCode === cptCode) {
                return { ...r, amount: suggestedAmount };
              }
              return r;
            });
          dispatch(setRows(updatedRows));
        }, 100);
      }

      toastElement?.show({
        severity: "success",
        summary: "Implant Added",
        detail: `Added ${cptCode} to the estimate.`,
        life: 3000,
      });
    } catch (error) {
      console.error(`Error adding implant ${cptCode}:`, error);
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to add implant code ${cptCode}.`,
        life: 5000,
      });
    } finally {
      setLoadingCptCode(null);
    }
  };

  return (
    <StepCard
      status={status}
      icon={<StethoscopeIcon width={20} height={20} />}
      title="Services"
      description="Add CPT codes to build the charge estimate"
      data-testid="serviceDetails"
    >
      <div className="formgrid grid w-100">
        <div className="field p-fluid col-12" data-tour={TourAnchor.CptSearch}>
          {/*
              Visit-date CPT gate (off for now): re-import CPT_SEARCH_REQUIRES_VISIT_DATE_MESSAGE +
              isDateOfServiceValidForCptSearch, restore orderType watch + memos + canSearchCptByVisitDate,
              use disabled={searchLoading || !canSearchCptByVisitDate}, cptHelperText, cptHelperError;
              in payer/DOS effect, restore `if (!dateOfServiceForSearch) return`.
            */}
          <AutoCompleteCPT
            search={(e) => {
              setUnavailableCptCodes([]);
              setUnavailableCptRemoved(false);
              search(e);
            }}
            disabled={searchLoading}
            setCurrentProviderProcedures={setCurrentProviderProcedures}
            autoCompleteValue={autoCompleteValue}
            procedures={procedures}
            providerProcedures={providerProcedures}
            currentProviderProcedures={currentProviderProcedures}
          />
          {commonCodeRecommendationsEnabled && (
            <CptRecommendations
              onRecommendationClick={handleRecommendationClick}
              onQuantityClick={handleQuantityRecommendationClick}
              loadingCptCode={loadingCptCode}
            />
          )}
          {accountData?.data?.features?.implantSuggestions && (
            <ImplantSuggestion
              cptCodes={buildSelectedProceduresObject(autoCompleteValue).map(
                (item) => item.code
              )}
              cptQuantities={getCptQuantities(rows)}
              payerId={payerField?.payerId}
              payerName={payerNameField}
              accountId={accountId}
              onAddImplant={handleAddImplant}
              isLoading={searchLoading}
              disabled={searchLoading}
            />
          )}
          {!!unavailableCptCodes.length && !searchLoading && (
            <p
              className="mt-1 text-orange-500 text-sm"
              data-testid="unavailableCptCodesMessage"
            >
              {buildUnavailableCptMessage({
                unavailableCptCodes,
                removedFromTable: unavailableCptRemoved,
              })}
            </p>
          )}
          {searchLoading && (
            <LoadingSpinner
              customStyle={{
                width: "30px",
                height: "30px",
              }}
            />
          )}
        </div>
      </div>

      {dataTableRows.length ? (
        <StyledServicesTable data-tour={TourAnchor.ServicesTable}>
          <DataTable
            stripedRows
            value={cptGroups}
            dataKey="cptCode"
            expandedRows={expandedBundles}
            onRowToggle={() => {}}
            rowExpansionTemplate={(group) => bundleTemplate(group)}
            responsiveLayout="scroll"
          >
            <Column
              field="cptCode"
              header="CPT"
              headerStyle={{ width: "110px" }}
              body={(group) => cptCodeTemplate(group)}
            />
            <Column
              header="Description"
              body={(group) =>
                buildDescriptionColumnTemplate({
                  row: group.rows[0],
                  rows,
                  dispatch,
                  priorAuthByCode,
                })
              }
              style={{
                minWidth: "270px",
                maxWidth: "270px",
                paddingLeft: sizer(6),
              }}
            />
            <Column
              header="Benefit"
              headerStyle={{ width: "220px" }}
              body={(group) => benefitTemplate(group)}
            />
            <Column
              header="Amount"
              body={(group) => amountCellTemplate(group)}
              headerStyle={{ width: "150px" }}
            />
            <Column
              header="Provider"
              body={(group) => providerCellTemplate(group)}
            />
            {/* Per-procedure CPT modifier input is hidden for now (CLE-2178).
              Pricing-engine + claim-classification support stays; re-add a
              <Column> rendering <ModifiersInput row={row} /> when product wants
              order-level modifier entry. */}
            <Column
              header="Units"
              headerStyle={{ width: "110px" }}
              body={(group) => quantityTemplate(group)}
            />
            <Column
              style={{ width: "56px", textAlign: "right" }}
              body={(group) => actionCellTemplate(group)}
            />
          </DataTable>
          <ServicesFooter rows={dataTableRows} />
        </StyledServicesTable>
      ) : (
        <NoServices />
      )}
      {(priceBreakdownRows.adjustments.length > 0 ||
        excludedNcciInfo.length > 0) && (
        <AdjustmentDetails
          displayAdjustments={displayAdjustments}
          priceBreakdownRows={priceBreakdownRows}
          dispatch={dispatch}
          priceExecuting={priceExecuting}
          excludedNcciInfo={excludedNcciInfo}
          onNcciToggle={(
            rulesetId: string,
            info: { cptCode: string; discountedCptCode: string }
          ) => {
            if (excludedNcciRulesetIds.includes(rulesetId)) {
              setExcludedNcciRulesetIds((prev) =>
                prev.filter((id) => id !== rulesetId)
              );
              setExcludedNcciInfo((prev) =>
                prev.filter((item) => item.rulesetId !== rulesetId)
              );
            } else {
              setExcludedNcciRulesetIds((prev) => [...prev, rulesetId]);
              setExcludedNcciInfo((prev) => [...prev, { rulesetId, ...info }]);
            }
          }}
        />
      )}

      <PatientResponsibilityDetails
        displayPatientResponsibility={displayPatientResponsibility}
        priceBreakdownRows={priceBreakdownRows}
        dispatch={dispatch}
        priceExecuting={priceExecuting}
      />
    </StepCard>
  );
}
